import React, { useState, useEffect } from 'react';
import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps';
import { fetchStreets, saveStreet, deleteStreet } from '../services/firestoreService';
import { geocodeAddress, reverseGeocode } from '../services/geocode';
import { getUserIP } from '../services/ipService';
import { Button, Container, Tab, Tabs, Typography, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, List, ListItem, ListItemText, IconButton } from '@mui/material';
import SearchBar from './SearchBar';
import MusicPlayer from './MusicPlayer';

const YandexMap = () => {
  const [mapState, setMapState] = useState({
    center: [40.7411, 44.8630],
    zoom: 15,
  });

  const [streetsWithoutWater, setStreetsWithoutWater] = useState([]);
  const [streetsWithoutLight, setStreetsWithoutLight] = useState([]);
  const [currentStreets, setCurrentStreets] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [userIP, setUserIP] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [selectedMarkerName, setSelectedMarkerName] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchStreets();
      const waterStreets = data.filter(street => street.type === 'water');
      const lightStreets = data.filter(street => street.type === 'light');
      setStreetsWithoutWater(waterStreets);
      setStreetsWithoutLight(lightStreets);
    };

    const fetchIP = async () => {
      const ip = await getUserIP();
      setUserIP(ip);
    };

    fetchData();
    fetchIP();
  }, []);

  const handleMapClick = async(e) => {
    const coords = e.get('coords');
    // const address = reverseGeocode(coords);
    setCurrentStreets([...currentStreets, { name: coords , coordinates: coords }]);
  };

  const handleSaveStreet = async () => {
    for (const street of currentStreets) {
      await saveStreet(street, tabIndex === 0 ? 'water' : 'light', userIP);
    }
    if (tabIndex === 0) {
      setStreetsWithoutWater([...streetsWithoutWater, ...currentStreets]);
    } else {
      setStreetsWithoutLight([...streetsWithoutLight, ...currentStreets]);
    }
    setCurrentStreets([]);
    setSaveDialogOpen(false);
  };

  const handleCancelSave = () => {
    setSaveDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleMarkerClick = (index) => {
    const updatedStreets = currentStreets.filter((_, i) => i !== index);
    setCurrentStreets(updatedStreets);
  };

  const handleSavedMarkerClick = (id, name) => {
      setSelectedMarkerId(id);
      setSelectedMarkerName(name);
      setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    await deleteStreet(selectedMarkerId);
    if (tabIndex === 0) {
      setStreetsWithoutWater(streetsWithoutWater.filter(street => street.id !== selectedMarkerId));
    } else {
      setStreetsWithoutLight(streetsWithoutLight.filter(street => street.id !== selectedMarkerId));
    }
    setDeleteDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  const handleSearch = async (address) => {
    try {
      const coordinates = await geocodeAddress(address);
      setMapState((prevState) => ({
        ...prevState,
        center: coordinates,
        zoom: 17,
      }));
      setCurrentStreets([...currentStreets, { name: `Street ${currentStreets.length + 1}`, coordinates, userIP }]);
    } catch (error) {
      console.error('Address not found');
    }
  };

  const handleOpenSaveDialog = () => {
    setSaveDialogOpen(true);
  };

  return (
    <Container>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={handleTabChange} centered>
          <Tab label="Улицы без воды" />
          <Tab label="Улицы без света" />
        </Tabs>
      </Box>
      <SearchBar onSearch={handleSearch} />
      <Box my={2} display="flex" alignItems="center">
        <Button variant="contained" color="primary" onClick={handleOpenSaveDialog}>
          Сохранить
        </Button>
        <MusicPlayer tabIndex={tabIndex} />
      </Box>
      <Typography variant="body2" color="textSecondary">
        Музыка любезно предоставлена <a href="https://utkorose.ru" target="_blank" rel="noopener noreferrer">Славелием Роза</a>
      </Typography>
      <Typography variant="h6">
        {tabIndex === 0 ? 'Улицы без воды' : 'Улицы без света'}
      </Typography>
      <YMaps>
        <Map
          state={mapState}
          width="100%"
          height="600px"
          onClick={handleMapClick}
        >
          {tabIndex === 0 && streetsWithoutWater.map((street, index) => (
            <Placemark
              key={index}
              geometry={street.coordinates}
              properties={{ balloonContent: street.name }}
              options={{ preset: 'islands#redIcon' }}
              onClick={() => handleSavedMarkerClick(street.id, street.name)}
            />
          ))}
          {tabIndex === 1 && streetsWithoutLight.map((street, index) => (
            <Placemark
              key={index}
              geometry={street.coordinates}
              properties={{ balloonContent: street.name }}
              options={{ preset: 'islands#blueIcon' }}
              onClick={() => handleSavedMarkerClick(street.id, street.name)}
            />
          ))}
          {currentStreets.map((street, index) => (
            <Placemark
              key={`current-${index}`}
              geometry={street.coordinates}
              properties={{ balloonContent: street.name }}
              options={{ preset: 'islands#greenIcon' }}
              onClick={() => handleMarkerClick(index)}
            />
          ))}
        </Map>
      </YMaps>
      <Dialog
        open={saveDialogOpen}
        onClose={handleCancelSave}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Сохранить?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Вы действительно хотите сохранить?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave} color="primary">
            Отмена
          </Button>
          <Button onClick={handleSaveStreet} color="primary" autoFocus>
            Да
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Удалить улицу?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Вы действительно хотите удалить следующую улицу?
          </DialogContentText>
          <List>
            <ListItem>
              <ListItemText primary={selectedMarkerName} />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Отмена
          </Button>
          <Button onClick={handleConfirmDelete} color="primary" autoFocus>
            Да
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default YandexMap;
